import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Shield, AlertTriangle, Globe, Activity, TrendingUp, TrendingDown,
  Server, Zap, Clock, RefreshCw, MapPin, Building, Target,
  ArrowUp, ArrowDown, Minus, ExternalLink, Info
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Simulated DDoS data - In production, this would come from threat intelligence APIs
const generateDDoSData = () => {
  const countries = [
    { code: 'US', name: 'United States', attacks: Math.floor(Math.random() * 5000) + 3000 },
    { code: 'DE', name: 'Germany', attacks: Math.floor(Math.random() * 4000) + 2000 },
    { code: 'UK', name: 'United Kingdom', attacks: Math.floor(Math.random() * 3500) + 1500 },
    { code: 'FR', name: 'France', attacks: Math.floor(Math.random() * 3000) + 1200 },
    { code: 'NL', name: 'Netherlands', attacks: Math.floor(Math.random() * 2500) + 1000 },
    { code: 'RU', name: 'Russia', attacks: Math.floor(Math.random() * 2000) + 800 },
    { code: 'CN', name: 'China', attacks: Math.floor(Math.random() * 2500) + 1000 },
    { code: 'JP', name: 'Japan', attacks: Math.floor(Math.random() * 1500) + 600 },
    { code: 'BR', name: 'Brazil', attacks: Math.floor(Math.random() * 1200) + 500 },
    { code: 'IN', name: 'India', attacks: Math.floor(Math.random() * 1000) + 400 },
  ];

  const industries = [
    { name: 'Technology', attacks: Math.floor(Math.random() * 8000) + 5000, share: 28.5 },
    { name: 'Financial Services', attacks: Math.floor(Math.random() * 6000) + 4000, share: 22.3 },
    { name: 'Government', attacks: Math.floor(Math.random() * 5000) + 3000, share: 18.7 },
    { name: 'Healthcare', attacks: Math.floor(Math.random() * 3000) + 1500, share: 12.4 },
    { name: 'E-commerce', attacks: Math.floor(Math.random() * 2500) + 1200, share: 9.8 },
    { name: 'Gaming', attacks: Math.floor(Math.random() * 2000) + 800, share: 5.2 },
    { name: 'Media', attacks: Math.floor(Math.random() * 1500) + 500, share: 3.1 },
  ];

  const attackTypes = [
    { type: 'SYN Flood', percentage: 25.5, color: '#ef4444' },
    { type: 'HTTP GET Flood', percentage: 24.8, color: '#f97316' },
    { type: 'ACK Flood', percentage: 12.9, color: '#eab308' },
    { type: 'HTTP POST Flood', percentage: 11.5, color: '#22c55e' },
    { type: 'UDP Flood', percentage: 10.5, color: '#3b82f6' },
    { type: 'DNS Amplification', percentage: 8.2, color: '#8b5cf6' },
    { type: 'ICMP Flood', percentage: 4.5, color: '#ec4899' },
    { type: 'Others', percentage: 2.1, color: '#6b7280' },
  ];

  const recentAttacks = [
    { target: 'api.fintech-corp.com', country: 'US', time: '2 min ago', severity: 'Critical', type: 'SYN Flood', bandwidth: '45.2 Gbps' },
    { target: 'portal.gov.de', country: 'DE', time: '5 min ago', severity: 'High', type: 'HTTP GET Flood', bandwidth: '28.7 Gbps' },
    { target: 'ecommerce.uk.net', country: 'UK', time: '8 min ago', severity: 'Medium', type: 'UDP Flood', bandwidth: '12.3 Gbps' },
    { target: 'healthcare-sys.fr', country: 'FR', time: '12 min ago', severity: 'Critical', type: 'ACK Flood', bandwidth: '67.8 Gbps' },
    { target: 'bank-api.nl', country: 'NL', time: '15 min ago', severity: 'High', type: 'DNS Amplification', bandwidth: '34.5 Gbps' },
    { target: 'gaming-server.jp', country: 'JP', time: '18 min ago', severity: 'Medium', type: 'SYN Flood', bandwidth: '15.2 Gbps' },
    { target: 'cloud-service.us', country: 'US', time: '22 min ago', severity: 'Critical', type: 'HTTP POST Flood', bandwidth: '89.1 Gbps' },
    { target: 'media-cdn.br', country: 'BR', time: '25 min ago', severity: 'Low', type: 'ICMP Flood', bandwidth: '5.8 Gbps' },
    { target: 'gov-portal.in', country: 'IN', time: '30 min ago', severity: 'High', type: 'SYN Flood', bandwidth: '42.3 Gbps' },
    { target: 'tech-startup.cn', country: 'CN', time: '35 min ago', severity: 'Medium', type: 'UDP Flood', bandwidth: '18.9 Gbps' },
  ];

  const monthlyTrend = [
    { month: 'Aug', attacks: 3200 },
    { month: 'Sep', attacks: 4100 },
    { month: 'Oct', attacks: 3800 },
    { month: 'Nov', attacks: 5200 },
    { month: 'Dec', attacks: 4500 },
    { month: 'Jan', attacks: 3967 },
  ];

  const threatActors = [
    { name: 'NoName057(16)', origin: 'Russia', targets: 'Government, Finance', activity: 'High', attacks: 1250 },
    { name: 'Killnet', origin: 'Russia', targets: 'NATO Countries', activity: 'Medium', attacks: 890 },
    { name: 'Anonymous Sudan', origin: 'Sudan', targets: 'Tech, Government', activity: 'High', attacks: 1100 },
    { name: 'IT Army of Ukraine', origin: 'Ukraine', targets: 'Russian Infrastructure', activity: 'High', attacks: 980 },
    { name: 'DDoSia Project', origin: 'Russia', targets: 'Western Countries', activity: 'Medium', attacks: 670 },
  ];

  return {
    countries: countries.sort((a, b) => b.attacks - a.attacks),
    industries: industries.sort((a, b) => b.attacks - a.attacks),
    attackTypes,
    recentAttacks,
    monthlyTrend,
    threatActors,
    stats: {
      totalAttacks: countries.reduce((sum, c) => sum + c.attacks, 0),
      avgAttacksPerHour: Math.floor(Math.random() * 10) + 3,
      peakBandwidth: (Math.random() * 500 + 300).toFixed(1),
      threatLevel: ['Low', 'Medium', 'High', 'Critical'][Math.floor(Math.random() * 4)],
    }
  };
};

const DDoSReport = () => {
  const [timeRange, setTimeRange] = useState('30d');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(new Date());

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, [timeRange]);

  const loadData = () => {
    setLoading(true);
    // Simulate API call
    setTimeout(() => {
      setData(generateDDoSData());
      setLastUpdated(new Date());
      setLoading(false);
    }, 500);
  };

  const getThreatLevelColor = (level) => {
    const colors = {
      'Low': 'bg-green-500',
      'Medium': 'bg-yellow-500',
      'High': 'bg-orange-500',
      'Critical': 'bg-red-500'
    };
    return colors[level] || 'bg-gray-500';
  };

  const getThreatLevelBg = (level) => {
    const colors = {
      'Low': 'bg-green-100 text-green-800 border-green-300',
      'Medium': 'bg-yellow-100 text-yellow-800 border-yellow-300',
      'High': 'bg-orange-100 text-orange-800 border-orange-300',
      'Critical': 'bg-red-100 text-red-800 border-red-300'
    };
    return colors[level] || 'bg-gray-100';
  };

  const getSeverityColor = (severity) => {
    const colors = {
      'Low': 'bg-green-100 text-green-700',
      'Medium': 'bg-yellow-100 text-yellow-700',
      'High': 'bg-orange-100 text-orange-700',
      'Critical': 'bg-red-100 text-red-700'
    };
    return colors[severity] || 'bg-gray-100';
  };

  const getCountryFlag = (code) => {
    // Return emoji flag
    const flags = {
      'US': '🇺🇸', 'DE': '🇩🇪', 'UK': '🇬🇧', 'FR': '🇫🇷', 'NL': '🇳🇱',
      'RU': '🇷🇺', 'CN': '🇨🇳', 'JP': '🇯🇵', 'BR': '🇧🇷', 'IN': '🇮🇳',
      'UA': '🇺🇦', 'PL': '🇵🇱', 'IT': '🇮🇹', 'ES': '🇪🇸', 'AU': '🇦🇺'
    };
    return flags[code] || '🌐';
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Shield className="w-8 h-8 text-red-500" />
            DDoS Threat Intelligence
          </h1>
          <p className="text-muted-foreground mt-1">
            Real-time DDoS attack monitoring and threat analysis
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">Last 7 Days</SelectItem>
              <SelectItem value="30d">Last 30 Days</SelectItem>
              <SelectItem value="90d">Last 3 Months</SelectItem>
              <SelectItem value="180d">Last 6 Months</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={loadData}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Last Updated */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Clock className="w-4 h-4" />
        Last updated: {lastUpdated.toLocaleTimeString()}
      </div>

      {/* Threat Level Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className={`border-2 ${getThreatLevelBg(data?.stats.threatLevel)}`}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium opacity-80">Global Threat Level</p>
                <p className="text-3xl font-bold mt-1">{data?.stats.threatLevel}</p>
                <p className="text-xs mt-2 opacity-70">{timeRange === '30d' ? '30' : timeRange === '7d' ? '7' : timeRange === '90d' ? '90' : '180'} days evaluation</p>
              </div>
              <div className={`w-16 h-16 rounded-full ${getThreatLevelColor(data?.stats.threatLevel)} flex items-center justify-center`}>
                <AlertTriangle className="w-8 h-8 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Attacks/Hour</p>
                <p className="text-3xl font-bold mt-1">{data?.stats.avgAttacksPerHour}</p>
                <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                  <TrendingDown className="w-3 h-3" /> -12% vs last period
                </p>
              </div>
              <Activity className="w-10 h-10 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Attacks</p>
                <p className="text-3xl font-bold mt-1">{data?.stats.totalAttacks.toLocaleString()}</p>
                <p className="text-xs text-red-600 mt-2 flex items-center gap-1">
                  <TrendingUp className="w-3 h-3" /> +8% vs last period
                </p>
              </div>
              <Zap className="w-10 h-10 text-yellow-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Peak Bandwidth</p>
                <p className="text-3xl font-bold mt-1">{data?.stats.peakBandwidth}</p>
                <p className="text-xs text-muted-foreground mt-2">Gbps</p>
              </div>
              <Server className="w-10 h-10 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Attack Types Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="w-5 h-5" />
              Top Attack Types
            </CardTitle>
            <CardDescription>Distribution of DDoS attack vectors</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {data?.attackTypes.map((attack, idx) => (
                <div key={idx} className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="font-medium">{attack.type}</span>
                    <span className="text-muted-foreground">{attack.percentage}%</span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div 
                      className="h-full rounded-full transition-all duration-500"
                      style={{ width: `${attack.percentage}%`, backgroundColor: attack.color }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Monthly Trend */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Monthly Attack Trend
            </CardTitle>
            <CardDescription>Attack volume over the past 6 months</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-48 flex items-end gap-2">
              {data?.monthlyTrend.map((month, idx) => {
                const maxAttacks = Math.max(...data.monthlyTrend.map(m => m.attacks));
                const height = (month.attacks / maxAttacks) * 100;
                return (
                  <div key={idx} className="flex-1 flex flex-col items-center gap-1">
                    <span className="text-xs text-muted-foreground">{month.attacks.toLocaleString()}</span>
                    <div 
                      className="w-full bg-gradient-to-t from-blue-600 to-blue-400 rounded-t transition-all duration-500 hover:from-blue-700 hover:to-blue-500"
                      style={{ height: `${height}%`, minHeight: '20px' }}
                    />
                    <span className="text-xs font-medium">{month.month}</span>
                  </div>
                );
              })}
            </div>
            <div className="mt-4 p-3 bg-muted rounded-lg flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Average/Month</span>
              <span className="font-bold">{Math.round(data?.monthlyTrend.reduce((sum, m) => sum + m.attacks, 0) / data?.monthlyTrend.length).toLocaleString()}</span>
            </div>
          </CardContent>
        </Card>

        {/* Top Targeted Countries */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="w-5 h-5" />
              Top 10 Targeted Countries
            </CardTitle>
            <CardDescription>Countries receiving the most attacks</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {data?.countries.slice(0, 10).map((country, idx) => (
                <div key={idx} className="flex items-center justify-between p-2 rounded-lg hover:bg-muted transition-colors">
                  <div className="flex items-center gap-3">
                    <span className="w-6 text-center text-sm text-muted-foreground">#{idx + 1}</span>
                    <span className="text-xl">{getCountryFlag(country.code)}</span>
                    <span className="font-medium">{country.name}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-bold">{country.attacks.toLocaleString()}</span>
                    {idx < 3 ? (
                      <TrendingUp className="w-4 h-4 text-red-500" />
                    ) : idx < 6 ? (
                      <Minus className="w-4 h-4 text-yellow-500" />
                    ) : (
                      <TrendingDown className="w-4 h-4 text-green-500" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Top Targeted Industries */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building className="w-5 h-5" />
              Top Targeted Industries
            </CardTitle>
            <CardDescription>Sectors most affected by DDoS attacks</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {data?.industries.map((industry, idx) => (
                <div key={idx} className="flex items-center justify-between p-2 rounded-lg hover:bg-muted transition-colors">
                  <div className="flex items-center gap-3">
                    <span className="w-6 text-center text-sm text-muted-foreground">#{idx + 1}</span>
                    <span className="font-medium">{industry.name}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge variant="outline">{industry.share}%</Badge>
                    <span className="font-bold w-16 text-right">{industry.attacks.toLocaleString()}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Latest Attacks Feed */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-red-500" />
                Latest DDoS Attacks
                <span className="ml-2 w-2 h-2 bg-red-500 rounded-full animate-pulse" />
              </CardTitle>
              <CardDescription>Real-time attack feed</CardDescription>
            </div>
            <Badge variant="outline" className="text-red-600">Live Feed</Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-3 text-sm font-medium text-muted-foreground">Target</th>
                  <th className="text-left p-3 text-sm font-medium text-muted-foreground">Country</th>
                  <th className="text-left p-3 text-sm font-medium text-muted-foreground">Attack Type</th>
                  <th className="text-left p-3 text-sm font-medium text-muted-foreground">Bandwidth</th>
                  <th className="text-left p-3 text-sm font-medium text-muted-foreground">Time</th>
                  <th className="text-left p-3 text-sm font-medium text-muted-foreground">Severity</th>
                </tr>
              </thead>
              <tbody>
                {data?.recentAttacks.map((attack, idx) => (
                  <tr key={idx} className="border-b hover:bg-muted/50 transition-colors">
                    <td className="p-3">
                      <code className="text-sm bg-muted px-2 py-1 rounded">{attack.target}</code>
                    </td>
                    <td className="p-3">
                      <span className="flex items-center gap-2">
                        {getCountryFlag(attack.country)}
                        <span className="text-sm">{attack.country}</span>
                      </span>
                    </td>
                    <td className="p-3 text-sm">{attack.type}</td>
                    <td className="p-3">
                      <span className="font-mono text-sm font-medium">{attack.bandwidth}</span>
                    </td>
                    <td className="p-3 text-sm text-muted-foreground">{attack.time}</td>
                    <td className="p-3">
                      <Badge className={getSeverityColor(attack.severity)}>{attack.severity}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Threat Actors */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-orange-500" />
            Active Threat Actors
          </CardTitle>
          <CardDescription>Known groups conducting DDoS campaigns</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-3 text-sm font-medium text-muted-foreground">Group Name</th>
                  <th className="text-left p-3 text-sm font-medium text-muted-foreground">Origin</th>
                  <th className="text-left p-3 text-sm font-medium text-muted-foreground">Primary Targets</th>
                  <th className="text-left p-3 text-sm font-medium text-muted-foreground">Activity Level</th>
                  <th className="text-left p-3 text-sm font-medium text-muted-foreground">Attacks ({timeRange})</th>
                </tr>
              </thead>
              <tbody>
                {data?.threatActors.map((actor, idx) => (
                  <tr key={idx} className="border-b hover:bg-muted/50 transition-colors">
                    <td className="p-3">
                      <span className="font-medium text-red-600">{actor.name}</span>
                    </td>
                    <td className="p-3 text-sm">{actor.origin}</td>
                    <td className="p-3 text-sm">{actor.targets}</td>
                    <td className="p-3">
                      <Badge className={
                        actor.activity === 'High' ? 'bg-red-100 text-red-700' :
                        actor.activity === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-green-100 text-green-700'
                      }>
                        {actor.activity}
                      </Badge>
                    </td>
                    <td className="p-3 font-bold">{actor.attacks.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Threat Landscape Summary */}
      <Card className="bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-white">
            <Info className="w-5 h-5" />
            📊 Threat Landscape Summary
          </CardTitle>
          <CardDescription className="text-slate-300">
            Updated {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="p-4 bg-white/10 rounded-lg">
              <p className="text-sm text-slate-300">Current Threat Level</p>
              <p className={`text-2xl font-bold ${
                data?.stats.threatLevel === 'Critical' ? 'text-red-400' :
                data?.stats.threatLevel === 'High' ? 'text-orange-400' :
                data?.stats.threatLevel === 'Medium' ? 'text-yellow-400' :
                'text-green-400'
              }`}>{data?.stats.threatLevel}</p>
            </div>
            <div className="p-4 bg-white/10 rounded-lg">
              <p className="text-sm text-slate-300">Top Targeted Sector</p>
              <p className="text-2xl font-bold text-blue-400">{data?.industries[0]?.name}</p>
            </div>
            <div className="p-4 bg-white/10 rounded-lg">
              <p className="text-sm text-slate-300">Attack Volume Change</p>
              <p className="text-2xl font-bold text-green-400">-12.4%</p>
            </div>
          </div>

          <div className="space-y-3">
            <h4 className="font-semibold text-white">Key Intelligence Insights</h4>
            <ul className="space-y-2 text-sm text-slate-300">
              <li className="flex gap-2">
                <span className="text-blue-400">▸</span>
                The threat landscape over the last {timeRange === '30d' ? '30 days' : timeRange === '7d' ? '7 days' : timeRange === '90d' ? '3 months' : '6 months'} indicates a significant concentration of DDoS attacks primarily targeting the {data?.industries[0]?.name.toLowerCase()} sector, accounting for over {data?.industries[0]?.share}% of unique targets.
              </li>
              <li className="flex gap-2">
                <span className="text-blue-400">▸</span>
                {data?.countries[0]?.name} and {data?.countries[1]?.name} emerge as the most affected countries, with over {Math.min(data?.countries[0]?.attacks, data?.countries[1]?.attacks).toLocaleString()} attacks each, highlighting regional vulnerabilities.
              </li>
              <li className="flex gap-2">
                <span className="text-blue-400">▸</span>
                The predominant attack types are {data?.attackTypes[0]?.type} and {data?.attackTypes[1]?.type}, suggesting a trend towards exploiting connection-oriented protocols and application layer vulnerabilities.
              </li>
              <li className="flex gap-2">
                <span className="text-blue-400">▸</span>
                Given the overall {data?.stats.threatLevel?.toLowerCase()} threat level, organizations in the {data?.industries[0]?.name.toLowerCase()} and {data?.industries[1]?.name.toLowerCase()} sectors should enhance their defenses against these prevalent DDoS tactics.
              </li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DDoSReport;
